import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import {
  Loader, Edit2, X, Plus, Search,
  Thermometer, Gauge, CloudRain, Compass, Droplets, Zap, Sun, Wind,
  Waves, FlaskConical, Circle, Leaf, Battery, Cpu, Layers,
} from 'lucide-react'
import { projectsApi } from '../lib/projectsApi'
import { useDashboardContext } from '../context/DashboardContext'

/**
 * "Current Conditions" view.
 *
 * Per-site card showing the latest reading of each **selected** parameter
 * (filtered by the sidebar's `selectedParameters`) as a metric card on the
 * left, alongside a site photo/webcam panel on the right.
 *
 * Site media (webcam URL or uploaded photo) is persisted in localStorage
 * keyed by (projectId, siteId), so it survives reloads without a schema
 * change. Moving it to a backend column is a future step.
 */
export function CurrentConditionsView({
  projectId = null,
  sites = [],
  selectedParameters = [],
  onTogglePanel = null,   // (paramId) => void — adds/removes a parameter panel
  dateRange = {},
  timezone = 'UTC',
  projectImage,
}) {
  // The per-site edit pencil only shows in dashboard Edit Mode (and for
  // editors). Read-only viewers / public contexts default to isCustomizing=false.
  const { isCustomizing = false, canEdit = true } = useDashboardContext()
  const [latest, setLatest] = useState({})       // { [paramId]: { value, unit, ts } }
  const [loading, setLoading] = useState(false)

  // Per-site media store loaded from localStorage on mount.
  // { [siteId]: { kind: 'webcam'|'photo', url: string } }
  const storageKey = `wqdata.currentConditions.media.${projectId || 'anon'}`
  const [siteMedia, setSiteMedia] = useState({})
  useEffect(() => {
    try {
      const raw = localStorage.getItem(storageKey)
      if (raw) setSiteMedia(JSON.parse(raw) || {})
    } catch { /* ignore malformed cache */ }
  }, [storageKey])
  const persistMedia = useCallback((next) => {
    setSiteMedia(next)
    try { localStorage.setItem(storageKey, JSON.stringify(next)) } catch {}
  }, [storageKey])

  // Menu / edit-mode / picker-modal state — keyed by site id so each site
  // has its own UI state and a click on site A doesn't affect site B.
  const [menuSiteId,  setMenuSiteId]  = useState(null)
  const [editSiteId,  setEditSiteId]  = useState(null)
  const [pickerModal, setPickerModal] = useState(null)  // { siteId, kind: 'webcam'|'photo' }
  const [addPanelSite, setAddPanelSite] = useState(null)  // Site row when Add-Panel modal is open

  // Site-card refs for the download / print handlers.
  const panelRefs = useRef({})

  // Per-site custom panel order — array of parameter ids. Params not
  // in this list fall back to their sidebar order.
  const orderStorageKey = `wqdata.currentConditions.order.${projectId || 'anon'}`
  const [panelOrder, setPanelOrder] = useState({})  // { [siteId]: [paramId, …] }
  useEffect(() => {
    try {
      const raw = localStorage.getItem(orderStorageKey)
      if (raw) setPanelOrder(JSON.parse(raw) || {})
    } catch { /* ignore malformed cache */ }
  }, [orderStorageKey])
  const persistOrder = useCallback((next) => {
    setPanelOrder(next)
    try { localStorage.setItem(orderStorageKey, JSON.stringify(next)) } catch {}
  }, [orderStorageKey])

  // Drag-drop tracking — records the source panel key so `onDrop` on
  // any other card knows what to reinsert.
  const dragState = useRef({ siteId: null, paramId: null })

  // ─── Selected params grouped by site ──────────────────────────────────
  const sitesWithSelected = useMemo(() => {
    const selectedIds = new Set((selectedParameters || []).map(p => String(p.id)))
    if (selectedIds.size === 0) return []
    return sites
      .map(site => {
        const params = []
        ;(site.devices || []).forEach(dev =>
          (dev.sensors || []).forEach(sensor =>
            (sensor.parameters || []).forEach(p => {
              if (selectedIds.has(String(p.id))) {
                params.push({ ...p, _sensorName: sensor.name })
              }
            })
          )
        )
        return { ...site, _params: params }
      })
      .filter(s => s._params.length > 0)
      .map((site) => {
        // Apply any custom drag-and-drop order. Params not covered by
        // the stored list fall through to their original position, so
        // adding a new panel appends it at the end.
        const stored = panelOrder[site.id]
        if (!Array.isArray(stored) || !stored.length) return site
        const indexOf = new Map(stored.map((id, i) => [String(id), i]))
        const withKey = site._params.map((p, i) => ({
          p,
          rank: indexOf.has(String(p.id)) ? indexOf.get(String(p.id)) : (stored.length + i),
        }))
        withKey.sort((a, b) => a.rank - b.rank)
        return { ...site, _params: withKey.map((x) => x.p) }
      })
  }, [sites, selectedParameters, panelOrder])

  // Drop callback: reorder `srcId` before `destId` in this site's list,
  // then persist.
  const reorderPanel = (siteId, srcId, destId) => {
    if (srcId == null || destId == null || String(srcId) === String(destId)) return
    const siteRow = sitesWithSelected.find((s) => String(s.id) === String(siteId))
    if (!siteRow) return
    const ids = siteRow._params.map((p) => String(p.id))
    const src = ids.indexOf(String(srcId))
    const dst = ids.indexOf(String(destId))
    if (src < 0 || dst < 0) return
    const next = ids.slice()
    const [moved] = next.splice(src, 1)
    // Drop BEFORE the destination when dragging leftwards, AFTER when
    // dragging rightwards — mirrors how most grid drag-drops behave.
    next.splice(src < dst ? dst : dst, 0, moved)
    persistOrder({ ...panelOrder, [siteId]: next })
  }

  const paramIdsKey = useMemo(
    () => sitesWithSelected.flatMap(s => s._params.map(p => p.id)).join(','),
    [sitesWithSelected]
  )

  useEffect(() => {
    const allParams = sitesWithSelected.flatMap(s => s._params)
    if (allParams.length === 0) { setLatest({}); return }

    let cancelled = false
    setLoading(true)

    Promise.allSettled(
      allParams.map(p =>
        projectsApi
          .fetchGraphData(Number(p.id), { fromDate: dateRange.from, toDate: dateRange.to, timezone })
          .then(res => ({
            id: p.id,
            value: (res && typeof res === 'object' && !res.error) ? res.latestValue : null,
            unit: (res && res.unit) || p.unit || '',
            ts: (res && res.latestTimestamp) || null,
          }))
      )
    ).then(results => {
      if (cancelled) return
      const map = {}
      results.forEach(r => {
        if (r.status === 'fulfilled' && r.value && r.value.id != null) map[r.value.id] = r.value
      })
      setLatest(map)
      setLoading(false)
    })

    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramIdsKey, dateRange.from, dateRange.to, timezone])

  // Close any open menu on outside click. One handler for the whole view.
  useEffect(() => {
    const onDocClick = (e) => {
      if (!e.target.closest?.('.site-panel-menu-anchor')) setMenuSiteId(null)
    }
    document.addEventListener('mousedown', onDocClick)
    return () => document.removeEventListener('mousedown', onDocClick)
  }, [])

  const fmtVal = (paramId, defaultUnit) => {
    const rec = latest[paramId]
    if (!rec || rec.value == null) return { value: '—', unit: defaultUnit || '' }
    const n = Number(rec.value)
    const v = Number.isFinite(n) ? (Math.abs(n) < 10 ? n.toFixed(2) : n.toFixed(1)) : String(rec.value)
    return { value: v, unit: rec.unit || defaultUnit || '' }
  }

  // ─── Download / Print helpers ─────────────────────────────────────────
  const capturePanel = async (siteId) => {
    const el = panelRefs.current[siteId]
    if (!el) return null
    // Dynamic import — same pattern as ReportView / DashboardWidgets.
    const html2canvas = (await import('html2canvas')).default
    return html2canvas(el, {
      backgroundColor: '#ffffff', scale: 2, useCORS: true, allowTaint: true, logging: false,
    })
  }

  const downloadPNG = async (siteId, siteName) => {
    setMenuSiteId(null)
    try {
      const canvas = await capturePanel(siteId)
      if (!canvas) return
      canvas.toBlob((blob) => {
        if (!blob) return
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url; a.download = `${siteName || 'site'}.png`
        document.body.appendChild(a); a.click(); a.remove()
        setTimeout(() => URL.revokeObjectURL(url), 1500)
      }, 'image/png')
    } catch (e) { console.error('[CurrentConditions PNG]', e) }
  }

  const downloadPDF = async (siteId, siteName) => {
    setMenuSiteId(null)
    try {
      const canvas = await capturePanel(siteId)
      if (!canvas) return
      const jsPDFmod = await import('jspdf')
      const jsPDF = jsPDFmod.jsPDF || jsPDFmod.default
      const doc = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'pt',
        format: 'letter',
      })
      const pageW = doc.internal.pageSize.getWidth()
      const pageH = doc.internal.pageSize.getHeight()
      // Fit-to-page with 32pt margins on all sides.
      const margin = 32
      const availW = pageW - margin * 2
      const availH = pageH - margin * 2
      const ratio  = Math.min(availW / canvas.width, availH / canvas.height)
      const drawW  = canvas.width  * ratio
      const drawH  = canvas.height * ratio
      const img    = canvas.toDataURL('image/png')
      doc.addImage(img, 'PNG', (pageW - drawW) / 2, margin, drawW, drawH)
      doc.save(`${siteName || 'site'}.pdf`)
    } catch (e) { console.error('[CurrentConditions PDF]', e) }
  }

  const printPanel = async (siteId, siteName) => {
    setMenuSiteId(null)
    try {
      const canvas = await capturePanel(siteId)
      if (!canvas) return
      const dataUrl = canvas.toDataURL('image/png')
      const w = window.open('', '_blank', 'noopener,noreferrer')
      if (!w) return
      w.document.write(`<!doctype html><html><head><title>${siteName || 'Site'}</title>
        <style>body{margin:0;padding:20px;font-family:system-ui,sans-serif;} img{max-width:100%;height:auto;}</style>
        </head><body onload="setTimeout(function(){window.print();},250)">
        <img src="${dataUrl}" alt="${siteName || 'Site'}"/></body></html>`)
      w.document.close()
    } catch (e) { console.error('[CurrentConditions Print]', e) }
  }

  // ─── Empty states ─────────────────────────────────────────────────────
  if ((selectedParameters || []).length === 0) {
    return (
      <div className="bg-white border-[#358ECD]/30 rounded-lg p-10 text-center text-gray-500 text-sm">
        Select one or more parameters from the sidebar to view current conditions.
      </div>
    )
  }
  if (sitesWithSelected.length === 0) {
    return (
      <div className="bg-white border-[#358ECD]/30 rounded-lg p-10 text-center text-gray-500 text-sm">
        The selected parameters don't belong to any site in this project.
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/*<h3 className="text-lg font-bold text-gray-800">Current Conditions</h3>*/}

      {loading && (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader className="w-4 h-4 animate-spin" /> Loading latest readings…
        </div>
      )}

      {sitesWithSelected.map(site => {
        const isEditing = editSiteId === site.id
        const media     = siteMedia[site.id]
        return (
          <div
            key={site.id}
            ref={(el) => { panelRefs.current[site.id] = el }}
            className={`bg-white rounded-lg ${isEditing ? 'border-[#358ECD]' : 'border-[#358ECD]/30'}`}
          >
            {/* Station header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
              <h4 className="text-base font-semibold text-gray-700">{site.name}</h4>
              {/* Center-of-header helper text — only in edit mode, hints
                  at the drag-and-drop UX we support on the metric cards. */}
              {isEditing && (
                <div className="hidden sm:flex items-center gap-2 text-xs text-gray-500 italic">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8L22 12L18 16"></path>
                    <path d="M6 8L2 12L6 16"></path>
                    <line x1="2" y1="12" x2="22" y2="12"></line>
                  </svg>
                  Drag Panels to Reorder
                </div>
              )}
              {!isEditing ? (
                <div className="flex items-center gap-1 site-panel-menu-anchor relative">
                  {/* 3-dot menu */}
                  <button
                    type="button"
                    title="More options"
                    onClick={() => setMenuSiteId(menuSiteId === site.id ? null : site.id)}
                    className="p-1.5 text-[#4D4D4D] hover:text-[#4D4D4D] hover:bg-gray-100 rounded transition"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="12" cy="5" r="2" /><circle cx="12" cy="12" r="2" /><circle cx="12" cy="19" r="2" />
                    </svg>
                  </button>
                  {menuSiteId === site.id && (
                    <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-200 rounded-lg shadow-xl z-30 py-1 text-sm text-gray-700">
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-50"
                        onClick={() => downloadPDF(site.id, site.name)}
                      >Download PDF</button>
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-50"
                        onClick={() => downloadPNG(site.id, site.name)}
                      >Download PNG Image</button>
                      <div className="my-1 border-t border-gray-100" />
                      <button
                        className="w-full text-left px-4 py-2 hover:bg-gray-50"
                        onClick={() => printPanel(site.id, site.name)}
                      >Print</button>
                    </div>
                  )}

                  {/* Pencil / edit — only in dashboard Edit Mode (and for editors) */}
                  {isCustomizing && canEdit && (
                    <button
                      type="button"
                      title="Edit — change site photo or webcam"
                      onClick={() => { setEditSiteId(site.id); setMenuSiteId(null) }}
                      className="p-1.5 text-gray-400 hover:text-[#358ECD] hover:bg-gray-100 rounded transition"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setEditSiteId(null)}
                    className="common-size15 px-3 py-1.5 text-xs font-semibold rounded bg-[#358ECD] hover:bg-[#2A78B0] text-white"
                  >Save</button>
                  <button
                    type="button"
                    onClick={() => setEditSiteId(null)}
                    className="common-size15 px-3 py-1.5 text-xs font-semibold text-gray-600 hover:underline"
                  >Cancel</button>
                </div>
              )}
            </div>

            {/* Body */}
            <div className="p-4 flex flex-col lg:flex-row gap-4">
              <div className="lg:w-2/5 flex flex-col gap-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 content-start">
                  {site._params.map(p => {
                    const { value, unit } = fmtVal(p.id, p.unit)
                    return (
                      <div
                        key={p.id}
                        draggable={isEditing}
                        onDragStart={isEditing ? (e) => {
                          dragState.current = { siteId: site.id, paramId: p.id }
                          e.dataTransfer.effectAllowed = 'move'
                          try { e.dataTransfer.setData('text/plain', String(p.id)) } catch {}
                        } : undefined}
                        onDragOver={isEditing ? (e) => {
                          if (dragState.current.siteId === site.id) {
                            e.preventDefault()
                            e.dataTransfer.dropEffect = 'move'
                          }
                        } : undefined}
                        onDrop={isEditing ? (e) => {
                          e.preventDefault()
                          const src = dragState.current
                          dragState.current = { siteId: null, paramId: null }
                          if (src.siteId === site.id) reorderPanel(site.id, src.paramId, p.id)
                        } : undefined}
                        className={`relative border rounded-lg p-3 bg-white transition-colors ${
                          isEditing
                            ? 'border-[#358ECD] cursor-move'
                            : 'border-gray-200 hover:border-[#358ECD]/40'
                        }`}
                      >
                        {isEditing && onTogglePanel && (
                          <button
                            type="button"
                            onClick={() => onTogglePanel(p.id)}
                            title="Remove this panel"
                            className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-white border border-gray-300 shadow-sm flex items-center justify-center text-gray-500 hover:text-red-600 hover:border-red-300"
                          >
                            <X className="w-3 h-3" />
                          </button>
                        )}
                        <div className="text-[11px] text-gray-500 uppercase tracking-wide truncate" title={p.name}>
                          {p.name}
                        </div>
                        <div className="mt-1 flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-gray-900 leading-tight">{value}</span>
                          {unit && <span className="text-xs text-gray-500">{unit}</span>}
                        </div>
                      </div>
                    )
                  })}
                </div>

                {/* + Add Panel — only in edit mode, only when we have a
                    toggle callback. Opens the Add Panel modal for this site. */}
                {isEditing && onTogglePanel && (
                  <button
                    type="button"
                    onClick={() => setAddPanelSite(site)}
                    className="self-start inline-flex items-center gap-1 text-sm font-semibold text-[#358ECD] hover:text-[#2A78B0]"
                  >
                    <Plus className="w-4 h-4" /> Add Panel
                  </button>
                )}
              </div>

              {/* Site photo / webcam pane */}
              <div className="lg:w-3/5 relative">
                <div className="bg-gray-100 rounded-lg overflow-hidden h-72 lg:h-full min-h-[280px] flex items-center justify-center">
                  {media?.kind === 'webcam' ? (
                    // EWC webcams push MP4 clips as well as JPG snapshots, so
                    // render a <video> for video URLs and an <img> otherwise.
                    /\.(mp4|webm|mov|m4v|avi)(\?|$)/i.test(media.url || '') ? (
                      <video src={media.url} className="w-full h-full object-cover" controls muted playsInline preload="metadata" />
                    ) : (
                      <img src={media.url} alt={`${site.name} webcam`} className="w-full h-full object-cover" />
                    )
                  ) : media?.kind === 'photo' ? (
                    <img src={media.url} alt={`${site.name} site`} className="w-full h-full object-cover" />
                  ) : projectImage ? (
                    <img src={projectImage} alt={`${site.name} site`} className="w-full h-full object-cover" />
                  ) : (
                    <div className="text-center text-gray-400 text-sm px-6">
                      Connect a NexSens Environmental Webcam<br />or Edit to Add a Site Photo
                    </div>
                  )}

                  {isEditing && (
                    <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                      <div className="text-center text-white text-sm space-y-4">
                        <button
                          type="button"
                          onClick={() => setPickerModal({ siteId: site.id, kind: 'webcam' })}
                          className="block mx-auto px-6 py-2 bg-white/10 border border-white/40 hover:bg-white/20 rounded font-semibold"
                        >
                          Select a NexSens Environmental Webcam
                        </button>
                        <div className="text-xs opacity-80">or</div>
                        <button
                          type="button"
                          onClick={() => setPickerModal({ siteId: site.id, kind: 'photo' })}
                          className="block mx-auto px-6 py-2 bg-white/10 border border-white/40 hover:bg-white/20 rounded font-semibold"
                        >
                          Upload a Site Photo
                        </button>
                        {media && (
                          <button
                            type="button"
                            onClick={() => {
                              const next = { ...siteMedia }; delete next[site.id]; persistMedia(next)
                            }}
                            className="block mx-auto text-xs text-white/70 hover:text-white underline mt-2"
                          >Remove current photo/webcam</button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )
      })}

      {/* Modals */}
      {pickerModal?.kind === 'webcam' && (
        <WebcamPickerModal
          projectId={projectId}
          onClose={() => setPickerModal(null)}
          onSave={(url) => {
            persistMedia({ ...siteMedia, [pickerModal.siteId]: { kind: 'webcam', url } })
            setPickerModal(null)
          }}
        />
      )}
      {pickerModal?.kind === 'photo' && (
        <PhotoUploadModal
          onClose={() => setPickerModal(null)}
          onSave={(dataUrl) => {
            persistMedia({ ...siteMedia, [pickerModal.siteId]: { kind: 'photo', url: dataUrl } })
            setPickerModal(null)
          }}
        />
      )}
      {addPanelSite && (
        <AddPanelModal
          site={addPanelSite}
          selectedIds={new Set((selectedParameters || []).map((p) => String(p.id)))}
          onClose={() => setAddPanelSite(null)}
          onPick={(paramId) => {
            if (onTogglePanel) onTogglePanel(paramId)
            setAddPanelSite(null)
          }}
        />
      )}
    </div>
  )
}

// ─── Modal shells ─────────────────────────────────────────────────────────

function ModalShell({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-base font-bold text-gray-800">{title}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-4 h-4" /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  )
}

function WebcamPickerModal({ projectId, onClose, onSave }) {
  // Real cameras registered on the project's sites (NexSens EWC etc.) — the
  // same source the dashboard Webcam widget uses. Labelled by the camera's
  // 'EWC <serial>' name; "Custom URL…" stays for one-off image feeds.
  const [cameras, setCameras] = useState([])
  const [loading, setLoading] = useState(false)
  const [selected, setSelected] = useState('')   // 'cam_<id>' | '__custom__' | ''
  const [url, setUrl] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!projectId) return
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    let cancelled = false
    setLoading(true)
    fetch(`${API_URL}/api/data/camera/?site__project=${projectId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((d) => { if (!cancelled) setCameras(Array.isArray(d) ? d : (d.results || [])) })
      .catch(() => { if (!cancelled) setCameras([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [projectId])

  const isCustom = selected === '__custom__'
  const camLabel = (c) => c.name || (c.ftp_folder ? `EWC ${c.ftp_folder}` : `Camera ${c.id}`)

  const handleSelect = async () => {
    setError('')
    if (isCustom) { const u = url.trim(); if (u) onSave(u); return }
    if (!selected) return
    // A real camera has no single static URL — resolve its latest snapshot.
    const camId = selected.replace('cam_', '')
    const API_URL = import.meta.env.VITE_API_URL || ''
    const JWT_KEY = import.meta.env.VITE_JWT_STORAGE_KEY || 'auth_token'
    const token = localStorage.getItem(JWT_KEY)
    setBusy(true)
    try {
      const r = await fetch(`${API_URL}/api/data/camera/${camId}/latest/`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      })
      const d = await r.json()
      if (d?.url) onSave(d.url)
      else setError('This webcam has no image yet.')
    } catch {
      setError('Could not load the webcam image.')
    } finally {
      setBusy(false)
    }
  }

  const canSelect = !busy && (isCustom ? !!url.trim() : !!selected)

  return (
    <ModalShell title="Select Environmental Webcam" onClose={onClose}>
      <label className="block text-xs font-semibold text-gray-700 mb-1">Webcam</label>
      <select
        value={selected}
        onChange={(e) => { setSelected(e.target.value); setError('') }}
        disabled={loading}
        className="w-full border border-gray-200 rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD] disabled:bg-gray-50"
      >
        <option value="">{loading ? 'Loading webcams…' : 'Select Webcam'}</option>
        {cameras.map((c) => <option key={c.id} value={`cam_${c.id}`}>{camLabel(c)}</option>)}
        <option value="__custom__">Custom URL…</option>
      </select>
      {isCustom && (
        <input
          type="url"
          value={url}
          onChange={(e) => { setUrl(e.target.value); setError('') }}
          placeholder="https://…/webcam.jpg"
          className="w-full border border-gray-200 rounded px-3 py-2 text-sm mb-3 focus:outline-none focus:border-[#358ECD]"
        />
      )}
      {error && <p className="text-xs text-red-600 mb-2">{error}</p>}
      <button
        type="button"
        onClick={handleSelect}
        disabled={!canSelect}
        className="w-full bg-[#358ECD] hover:bg-[#2A78B0] disabled:bg-gray-300 text-white text-sm font-semibold rounded py-2"
      >
        {busy ? 'Loading…' : 'SELECT WEBCAM'}
      </button>
      <button type="button" onClick={onClose} className="block mx-auto mt-2 text-xs text-[#358ECD] hover:underline">Cancel</button>
    </ModalShell>
  )
}

function PhotoUploadModal({ onClose, onSave }) {
  const [dragging, setDragging] = useState(false)
  const [file, setFile]         = useState(null)
  const [error, setError]       = useState(null)

  const onPick = (f) => {
    if (!f) return
    if (!/^image\//.test(f.type)) { setError('Please pick an image file.'); return }
    if (f.size > 8 * 1024 * 1024) { setError('Image must be smaller than 8MB.'); return }
    setError(null); setFile(f)
  }

  const submit = () => {
    if (!file) return
    const reader = new FileReader()
    reader.onload = () => onSave(String(reader.result || ''))
    reader.readAsDataURL(file)
  }

  return (
    <ModalShell title="Upload Site Photo" onClose={onClose}>
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true) }}
        onDragLeave={() => setDragging(false)}
        onDrop={(e) => { e.preventDefault(); setDragging(false); onPick(e.dataTransfer?.files?.[0]) }}
        className={`block w-full h-32 rounded border-dashed flex items-center justify-center text-sm cursor-pointer ${
          dragging ? 'border-[#358ECD] bg-[#EBF5FB]' : 'border-gray-300 bg-gray-50 text-gray-500 hover:bg-gray-100'
        }`}
      >
        <input type="file" accept="image/*" hidden onChange={(e) => onPick(e.target.files?.[0])} />
        {file
          ? <span className="text-gray-800">{file.name}</span>
          : <span>Drag Attachments or Upload Featured Photo</span>}
      </label>
      {error && <div className="mt-2 text-xs text-red-600">{error}</div>}
      <button
        type="button"
        onClick={submit}
        disabled={!file}
        className="w-full mt-3 bg-[#358ECD] hover:bg-[#2A78B0] disabled:bg-gray-300 text-white text-sm font-semibold rounded py-2"
      >
        UPLOAD
      </button>
      <button type="button" onClick={onClose} className="block mx-auto mt-2 text-xs text-[#358ECD] hover:underline">Cancel</button>
    </ModalShell>
  )
}

// ─── Add Panel modal ──────────────────────────────────────────────────────
//
// Renders the full canonical panel catalog from the backend
// (WidgetCategory + MasterWidget) — Weather / Water / Soil / Other —
// exactly matching the admin's "Predefine widget" library. Each tile
// is enabled when the current site has a parameter whose name matches
// the panel name (case-insensitive substring match — "Air Temperature"
// matches "Air Temp (C)" too). Disabled tiles show a tooltip.
//
// Kept in sync with the backend: adding a widget row under Panels -
// Weather in Django admin surfaces here automatically.

// Best-effort icon per panel name — same taxonomy as the mockup.
const ICON_RULES = [
  [/air temp|temperature/i,         Thermometer],
  [/rain intensity/i,               CloudRain],
  [/rain|precip/i,                  CloudRain],
  [/barometric|pressure/i,          Gauge],
  [/heading|direction|compass/i,    Compass],
  [/humid/i,                        Droplets],
  [/lightning/i,                    Zap],
  [/solar|sun/i,                    Sun],
  [/wind/i,                         Wind],
  [/battery|volt|charge/i,          Battery],
  [/signal|rssi|diagnostic|cpu/i,   Cpu],
  [/chlorophyll|bga|algae|leaf/i,   Leaf],
  [/ammonium|ph\b|orp|conductance|conduct|salin|nitrate|tds|hydrocarb|oil/i, FlaskConical],
  [/turbid/i,                       Layers],
  [/oxygen|do\b/i,                  Droplets],
  [/level|water current|wave|flow|discharge|water/i, Waves],
  [/gps|location/i,                 Compass],
  [/moisture/i,                     Droplets],
  [/soil/i,                         Leaf],
  [/snow/i,                         CloudRain],
]
function pickIcon(name) {
  for (const [re, C] of ICON_RULES) if (re.test(String(name || ''))) return C
  return Circle
}

// Normalise a name for match purposes: lowercase, strip units in parens,
// collapse whitespace. Panel "Air Temperature" should match "Air
// Temperature (°C)" and "Air Temp (C)".
function normalizeName(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/\(.*?\)/g, ' ')
    .replace(/[^\w]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}
// Match a panel name against a parameter name — either direction can
// be a substring of the other, so "Level" matches "Water Level" and
// vice-versa. Prefers a whole-word hit but tolerates substrings.
function matchesPanel(paramName, panelName) {
  const p = normalizeName(paramName)
  const q = normalizeName(panelName).replace(/^(panels?|panel)\s*-\s*/, '')
  if (!p || !q) return false
  return p === q || p.includes(q) || q.includes(p)
}

function AddPanelModal({ site, selectedIds, onClose, onPick }) {
  const API_URL       = import.meta.env.VITE_API_URL       || 'https://wqlive.ezxdemo.com'
  const API_BASE_PATH = import.meta.env.VITE_API_BASE_PATH || '/api'

  const [query, setQuery] = useState('')
  const [catalog, setCatalog] = useState(null)  // [{category, widgets:[{id,name}]}]
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false
    setLoading(true); setError(null)
    // Endpoints are already-unauthenticated — same pattern as the
    // Formulas library fetch.
    Promise.all([
      fetch(`${API_URL}${API_BASE_PATH}/dashboard/widget-categories/?is_active=true&ordering=order`, { credentials: 'same-origin' }).then((r) => r.json()),
      fetch(`${API_URL}${API_BASE_PATH}/dashboard/master-widgets/?is_active=true`, { credentials: 'same-origin' }).then((r) => r.json()),
    ])
      .then(([catRes, wRes]) => {
        if (cancelled) return
        // Some DRF configs return {results: [...]} for pagination;
        // fall back to the raw array otherwise.
        const cats = Array.isArray(catRes) ? catRes : (catRes?.results || [])
        const widgets = Array.isArray(wRes) ? wRes : (wRes?.results || [])
        // Panels-only categories, in the order they appear in the admin.
        const wanted = ['weather', 'water', 'soil', 'other']
        // The list serializer returns `category_name` (a string), not
        // `category` (FK id). Match on the name instead.
        const bucketed = wanted
          .map((key) => cats.find((c) => c.category_key === key))
          .filter(Boolean)
          .map((cat) => ({
            ...cat,
            widgets: widgets
              .filter((w) => (
                w.category_name === cat.name
                || w.category === cat.id
                || w.category?.id === cat.id
              ))
              .filter((w) => w.is_active !== false)
              .sort((a, b) => (a.name || '').localeCompare(b.name || '')),
          }))
        setCatalog(bucketed)
      })
      .catch((e) => { if (!cancelled) setError(e?.message || 'Failed to load panel catalog') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Flatten site parameters ONCE so each tile can find a candidate
  // parameter for click-to-add. Panels whose canonical name doesn't
  // match anything on the site render greyed out.
  const siteParams = useMemo(() => {
    const out = []
    ;(site.devices || []).forEach((dev) =>
      (dev.sensors || []).forEach((sensor) =>
        (sensor.parameters || []).forEach((p) => {
          out.push({ ...p, _sensorName: sensor.name, _deviceName: dev.name })
        }),
      ),
    )
    return out
  }, [site])

  const resolveMatch = (panelName) => {
    // First candidate whose name matches AND isn't already displayed.
    return siteParams.find(
      (p) => matchesPanel(p.name, panelName) && !selectedIds.has(String(p.id)),
    ) || null
  }

  const filteredCatalog = useMemo(() => {
    if (!catalog) return null
    const q = query.trim().toLowerCase()
    if (!q) return catalog
    return catalog
      .map((cat) => ({
        ...cat,
        widgets: (cat.widgets || []).filter((w) =>
          (w.name || '').toLowerCase().includes(q),
        ),
      }))
      .filter((cat) => cat.widgets.length > 0)
  }, [catalog, query])

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-start justify-center p-4 pt-16" onClick={onClose}>
      <div className="bg-white rounded-lg shadow-2xl w-full max-w-3xl max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between gap-4">
          <h3 className="text-base font-bold text-gray-800">Add Panel</h3>
          <div className="relative flex-1 max-w-xs">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search Panels"
              className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-200 rounded focus:outline-none focus:border-[#358ECD]"
            />
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto space-y-5">
          {loading && (
            <div className="py-12 text-center text-sm text-gray-500 flex items-center justify-center gap-2">
              <Loader className="w-4 h-4 animate-spin" /> Loading panel catalog…
            </div>
          )}
          {error && !loading && (
            <div className="py-12 text-center text-sm text-red-600">
              {error}
            </div>
          )}
          {!loading && !error && filteredCatalog?.length === 0 && (
            <div className="py-12 text-center text-sm text-gray-500">
              No panels match "{query}".
            </div>
          )}

          {!loading && !error && (filteredCatalog || []).map((cat) => (
            <div key={cat.id}>
              <h4 className="text-sm font-bold text-gray-800 mb-2">{cat.name}</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {cat.widgets.map((w) => {
                  const Icon = pickIcon(w.name)
                  const match = resolveMatch(w.name)
                  const disabled = !match
                  return (
                    <button
                      key={w.id}
                      type="button"
                      disabled={disabled}
                      title={disabled
                        ? `No ${w.name} parameter reporting from this site's devices`
                        : `Add ${w.name} to this dashboard`}
                      onClick={() => match && onPick(match.id)}
                      className={`text-left rounded-lg overflow-hidden border transition ${
                        disabled
                          ? 'border-gray-200 opacity-40 cursor-not-allowed'
                          : 'border-gray-200 hover:border-[#358ECD] hover:shadow'
                      }`}
                    >
                      <div
                        className="h-16 flex items-center justify-center"
                        style={{ backgroundColor: cat.color || '#6B7280' }}
                      >
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="px-2 py-2">
                        <div className="text-xs font-semibold text-gray-800 truncate" title={w.name}>{w.name}</div>
                        {match?._deviceName && (
                          <div className="text-[10px] text-gray-500 truncate">{match._deviceName}</div>
                        )}
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
